import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useResolveLocale } from './useLocale';
import { useResolvePlace } from './useMaps';
import { useLocationStore } from '../store/useLocationStore';
import { isPostalCodeInput } from '../utils/address';

/**
 * Capture a new operating locale from the TopBar switcher's search field —
 * either a typed ZIP or a city picked from region autocomplete.
 *
 * Both inputs land in the same place: the resolved locale is recorded via
 * `setLocaleFromSearch`, which stamps the single provenance flag
 * `locationSource = 'postal'`. That keeps the crosshair and dish-card distances
 * OFF (a ZIP or city centroid is too coarse to promise an accurate distance)
 * and anchors the feed on the locale centroid rather than any stale device
 * coords. Does NOT touch the saved `location` Address.
 *
 * The two inputs differ only in how they reach a locale:
 *  - A ZIP resolves directly (`/locale?postalCode=`), same as it always has,
 *    and is remembered for the "From ZIP 80206" line.
 *  - A city is geocoded first (`useResolvePlace`) and resolved by centroid
 *    lat/lon, which the BE matches to the existing city locale via its
 *    spatial/`city,state,country` lookups. No ZIP is remembered — the locale's
 *    own name is the label.
 *
 * Device capture is intentionally NOT here: "using my current location" means
 * persisting precise coords + `locationSource = 'device'`, which unlocks both
 * distance and the crosshair — so the TopBar device button goes through
 * `useAddressCapture` (the same path as the dish-card distance gate and the
 * device-location guard), keeping a single source of truth for device-vs-ZIP.
 *
 * Error strings come from the `location` i18n namespace.
 */
export function useLocaleCapture(options: {
  onCaptured?: () => void;
} = {}) {
  const { t } = useTranslation('location');
  const setLocaleFromSearch = useLocationStore((s) => s.setLocaleFromSearch);

  const resolveLocaleMutation = useResolveLocale();
  const resolvePlaceMutation = useResolvePlace();

  const [query, setQuery] = useState('');
  const [searchError, setSearchError] = useState('');

  const handleSearchSubmit = async (text: string) => {
    setSearchError('');

    const trimmed = text.trim();
    if (!trimmed) {
      setSearchError(t('errors.searchRequired'));
      return;
    }

    try {
      const isZip = isPostalCodeInput(trimmed);
      // City path: geocode the picked description to a centroid first, then
      // resolve by lat/lon (+ whatever postal code the geocode yielded, which
      // the BE uses only as a fallback when its own reverse-geocode omits one).
      const params = isZip
        ? { postalCode: trimmed }
        : await (async () => {
            const address = await resolvePlaceMutation.mutateAsync(trimmed);
            if (address.lat == null || address.lon == null) {
              throw new Error('unresolvable');
            }
            return { lat: address.lat, lon: address.lon, postalCode: address.postalCode };
          })();

      const locale = await resolveLocaleMutation.mutateAsync(params);
      setLocaleFromSearch(locale, isZip ? trimmed : null);
      options.onCaptured?.();
    } catch (err) {
      // Prefer apiService's translated coded-error message when present (e.g.
      // COUNTRY_NOT_SUPPORTED for non-US input). Falls back to the generic
      // unresolved message.
      const apiErr = err as Error & { userMessage?: string };
      setSearchError(apiErr.userMessage ?? t('errors.searchUnresolved'));
    }
  };

  return {
    query,
    setQuery,
    searchError,
    handleSearchSubmit,
    searchSubmitting: resolvePlaceMutation.isPending || resolveLocaleMutation.isPending,
  };
}
